import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import DashboardPage from './DashboardPage';
import ProfileContainer from './ProfileContainer';
import AddCandidatePage from './AddCandidatePage';
import PendingSubmissionPage from './PendingSubmissionPage';
import LoginExpiredCandidatesPage from './LoginExpiredCandidatesPage';
import PendingCaseSubmissionPage from './PendingCaseSubmissionPage';
import SubmittedVerificationPage from './SubmittedVerificationPage';
import BgvInProgressCandidatesPage from './BgvInProgressCandidatesPage';
import BgvCompletedCandidatesPage from './BgvCompletedCandidatesPage';
import TotalCandidatesPage from './TotalCandidatesPage';
import TopNavbar from '../top-navbar/TopNav';
import ReportPage from './Report';
import FinalReportPage from './FinalReport';
import { sidebarItems } from './constants';
import '../../styles/user/clientdashboard.css';
import logo from '../../assets/images/Logo.png';

const ClientDashboard = () => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [activeMenuItem, setActiveMenuItem] = useState('Dashboard');
  const [openSubMenu, setOpenSubMenu] = useState(null);

  const [candidateData, setCandidateData] = useState({
    fullname: '',
    email: '',
    dateOfBirth: '',
    mobileNumber: '',
  });

  const [submissionStatus, setSubmissionStatus] = useState(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [redirectTimeout, setRedirectTimeout] = useState(null);
  const [totalCandidates, setTotalCandidates] = useState([]);
  const [fetchError, setFetchError] = useState(null);

  const [loginExpiredCandidates, setLoginExpiredCandidates] = useState([]);
  const [loginExpiredFetchError, setLoginExpiredFetchError] = useState(null);
  const [isLoadingLoginExpired, setIsLoadingLoginExpired] = useState(false);

  const [bgvInProgressCandidates, setBgvInProgressCandidates] = useState([]);
  const [bgvInProgressFetchError, setBgvInProgressFetchError] = useState(null);
  const [isLoadingBgvInProgress, setIsLoadingBgvInProgress] = useState(false);

  // New state for ReportPage
  const [showReportPage, setShowReportPage] = useState(false);
  const [reportCandidateId, setReportCandidateId] = useState(null); // To pass the ID to ReportPage

  const [showFinalReportPage, setShowFinalReportPage] = useState(false); // State to control FinalReportPage visibility
  const [finalReportCandidateId, setFinalReportCandidateId] = useState(null); // State to pass ID to FinalReportPage
  const toggleDropdown = () => setShowDropdown(!showDropdown);

  const handleMenuClick = (menuItem) => {
    setActiveMenuItem(menuItem);
    setOpenSubMenu((prev) => (prev === menuItem ? null : menuItem));
    setShowReportPage(false); // Hide report page when changing main menu
    setReportCandidateId(null); // Clear candidate ID
  };

  const handleCandidateChange = (e) => {
    const { name, value } = e.target;
    setCandidateData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelAddCandidate = () => {
    if (redirectTimeout) {
      clearTimeout(redirectTimeout);
      setRedirectTimeout(null);
    }
    setCandidateData({ fullname: '', email: '', dateOfBirth: '', mobileNumber: '' });
    setSubmissionStatus(null);
    setErrorMessage('');
    setActiveMenuItem('Dashboard');
  };

  // New handler to show ReportPage
  const handleShowReportPage = (candidateId) => {
    setActiveMenuItem('Report'); // Set active menu item to 'Report'
    setShowReportPage(true);
    setReportCandidateId(candidateId);
  };

  // Handler to go back from ReportPage (optional, but good for navigation)
  const handleBackFromReport = () => {
    setShowReportPage(false);
    setReportCandidateId(null);
    setActiveMenuItem('Submitted Verification'); // Go back to the previous page
  };

   // New handler to show FinalReportPage
  const handleShowFinalReportPage = (candidateId) => {
    setActiveMenuItem('Final Report'); // Set active menu item to 'Final Report'
    setShowFinalReportPage(true);
    setFinalReportCandidateId(candidateId);
    setShowReportPage(false); // Ensure regular report is hidden
  };

  // New handler to go back from FinalReportPage
  const handleBackFromFinalReport = () => {
    setShowFinalReportPage(false);
    setFinalReportCandidateId(null);
    setActiveMenuItem('Total Candidates'); // Go back to Total Candidates or another relevant page
  };

  const mainContentRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (mainContentRef.current) {
      const elements = mainContentRef.current.querySelectorAll('.animate-on-scroll');
      elements.forEach((el) => observer.observe(el));
    }

    return () => observer.disconnect();
  }, [activeMenuItem]);

  return (
    <div className="client-dashboard-container">
      <TopNavbar />

      <Sidebar
        sidebarItems={sidebarItems}
        activeMenuItem={activeMenuItem}
        openSubMenu={openSubMenu}
        handleMenuClick={handleMenuClick}
        setActiveMenuItem={setActiveMenuItem}
        setTotalCandidates={setTotalCandidates}
        setFetchError={setFetchError}
        setLoginExpiredCandidates={setLoginExpiredCandidates}
        setLoginExpiredFetchError={setLoginExpiredFetchError}
        setIsLoadingLoginExpired={setIsLoadingLoginExpired}
        setBgvInProgressCandidates={setBgvInProgressCandidates}
        setBgvInProgressFetchError={setBgvInProgressFetchError}
        setIsLoadingBgvInProgress={setIsLoadingBgvInProgress}
      />

      <main className="dashboard-main" ref={mainContentRef}>
        <div className="dashboard-content">
          {activeMenuItem === 'Dashboard' && !showReportPage && ( // Add !showReportPage to ensure it doesn't render simultaneously
            <DashboardPage
              totalCandidates={totalCandidates}
              loginExpiredCandidates={loginExpiredCandidates}
              bgvInProgressCandidates={bgvInProgressCandidates}
              dashboardData={{
                totalCandidates: 220,
                pendingSubmission: 12,
                pendingCaseSubmission: 8,
                submittedVerification: 35,
                loginExpired: 2,
                chartData: [
                  { label: 'Total Candidates', value: 42, color: '#6A1B9A' },
                  { label: 'Pending Submission', value: 25, color: '#FFC107' },
                  { label: 'Submitted for Verification', value: 13, color: '#007BFF' },
                  { label: 'Pending Case Submission', value: 7, color: '#28A745' },
                  { label: 'Login Expired Candidates', value: 3, color: '#DC3545' },
                  { label: 'Verification Completed', value: 10, color: '#17A2B8' },
                ],
              }}
              setActiveMenuItem={setActiveMenuItem}
            />
          )}

          {activeMenuItem === 'Profile' && !showReportPage && <ProfileContainer />}

          {activeMenuItem === 'Add Candidate' && !showReportPage && (
            <AddCandidatePage
              candidateData={candidateData}
              handleCandidateChange={handleCandidateChange}
              handleCancelAddCandidate={handleCancelAddCandidate}
              setActiveMenuItem={setActiveMenuItem}
              submissionStatus={submissionStatus}
              setSubmissionStatus={setSubmissionStatus}
              errorMessage={errorMessage}
              setErrorMessage={setErrorMessage}
              redirectTimeout={redirectTimeout}
              setRedirectTimeout={setRedirectTimeout}
            />
          )}

          {activeMenuItem === 'Pending Submission' && !showReportPage && <PendingSubmissionPage />}

          {activeMenuItem === 'Login Expired Candidates' && !showReportPage && (
            <LoginExpiredCandidatesPage
              candidates={loginExpiredCandidates}
              isLoading={isLoadingLoginExpired}
              fetchError={loginExpiredFetchError}
            />
          )}

          {activeMenuItem === 'Pending Case Submission' && !showReportPage && <PendingCaseSubmissionPage         onViewReportTwo={handleShowReportPage}
       />}

          {/* Pass handleShowReportPage to SubmittedVerificationPage */}
          {activeMenuItem === 'Submitted Verification' && !showReportPage && (
            <SubmittedVerificationPage
                  onViewReportTwo={handleShowReportPage}

              onViewReport={handleShowReportPage} // New prop
            />
          )}

          {activeMenuItem === 'BGV In Progress Candidates' && !showReportPage && (
            <BgvInProgressCandidatesPage
              candidates={bgvInProgressCandidates}
              isLoading={isLoadingBgvInProgress}
              fetchError={bgvInProgressFetchError}
            />
          )}
          {activeMenuItem === 'BGV Completed Candidates' && !showReportPage && <BgvCompletedCandidatesPage />}
          {activeMenuItem === 'Total Candidates' && !showReportPage && (
           <TotalCandidatesPage
              candidates={totalCandidates}
              fetchError={fetchError}
               onViewReportTwo={handleShowReportPage}
              onViewFinalReport={handleShowFinalReportPage} // New prop
            />
          )}
        </div>

        {/* Conditional rendering for ReportPage */}
        {showReportPage && activeMenuItem === 'Report' && (
          <ReportPage
            candidateId={reportCandidateId} // Pass the selected candidate ID
            onBack={handleBackFromReport} // Optional: Pass a function to go back
          />
        )}
   {showFinalReportPage && activeMenuItem === 'Final Report' && (
          <FinalReportPage
            candidateId={finalReportCandidateId} // Pass the selected candidate ID
            onBack={handleBackFromFinalReport} // Optional: Pass a function to go back
          />
        )}
      </main>
    </div>
  );
};

export default ClientDashboard;